import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { Product, CartItem, getDiscountedPrice } from '@/types/product';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product) => void;
  addToCartWithCustomUnit: (product: Product, quantity: number, customUnit: string) => void;
  removeFromCart: (productId: string, customUnit?: string) => void;
  updateQuantity: (productId: string, quantity: number, customUnit?: string) => void;
  updateCustomUnit: (productId: string, oldCustomUnit: string, newCustomUnit: string) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isLoading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'fermerchel_cart';

// Helper to normalize custom unit for comparison (e.g., "0.5 кг" and "0,5 кг" should match)
const normalizeUnit = (unit: string): string => {
  return unit.replace(',', '.').trim();
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [dbLoaded, setDbLoaded] = useState(false);

  // Load cart from localStorage for guests
  const loadLocalCart = useCallback((): CartItem[] => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, []);

  // Save cart to localStorage
  const saveLocalCart = useCallback((cartItems: CartItem[]) => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, []);

  // Load cart from database for authenticated users
  const loadCartFromDb = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      logger.debug('[Cart] Loading cart from DB for user:', user.id);
      const { data: cartData, error: cartError } = await supabase
        .from('cart_items')
        .select('product_id, quantity, custom_unit')
        .eq('user_id', user.id);

      if (cartError) {
        logger.error('[Cart] Error loading cart:', cartError);
        // If custom_unit column doesn't exist, try without it
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('cart_items')
          .select('product_id, quantity')
          .eq('user_id', user.id);
        
        if (fallbackError || !fallbackData || fallbackData.length === 0) {
          logger.debug('[Cart] No items found in fallback, setting empty');
          setItems([]);
          return;
        }
        
        const productIds = fallbackData.map(item => item.product_id);
        const { data: products } = await supabase
          .from('products')
          .select('*')
          .in('id', productIds);

        const cartItems: CartItem[] = fallbackData
          .map(cartItem => {
            const product = products?.find(p => p.id === cartItem.product_id);
            if (!product) return null;
            return { product, quantity: cartItem.quantity };
          })
          .filter((item): item is CartItem => item !== null);

        logger.debug('[Cart] Loaded from fallback:', cartItems.length, 'items');
        setItems(cartItems);
        return;
      }

      logger.debug('[Cart] Cart data from DB:', cartData);
      
      if (!cartData || cartData.length === 0) {
        logger.debug('[Cart] No items in DB, setting empty');
        setItems([]);
        return;
      }

      // Fetch product details
      const productIds = [...new Set(cartData.map(item => item.product_id))];
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('*')
        .in('id', productIds);

      if (productsError) {
        logger.error('[Cart] Error loading products:', productsError);
        return;
      }

      const cartItems: CartItem[] = cartData
        .map(cartItem => {
          const product = products?.find(p => p.id === cartItem.product_id);
          if (!product) return null;
          return { 
            product, 
            quantity: cartItem.quantity,
            customUnit: (cartItem as any).custom_unit || undefined
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null) as CartItem[];

      logger.debug('[Cart] Loaded', cartItems.length, 'items from DB');
      setItems(cartItems);
    } catch (error) {
      logger.error('[Cart] Error loading cart:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Sync local cart to database when user logs in
  const syncLocalCartToDb = useCallback(async () => {
    if (!user) return;

    const localCart = loadLocalCart();
    if (localCart.length === 0) return;

    try {
      for (const item of localCart) {
        // Try to upsert with custom_unit
        const { error } = await supabase
          .from('cart_items')
          .upsert({
            user_id: user.id,
            product_id: item.product.id,
            quantity: item.quantity,
            custom_unit: item.customUnit || null,
          }, {
            onConflict: 'user_id,product_id'
          });
        
        if (error) {
          // Fallback: try without custom_unit
          await supabase
            .from('cart_items')
            .upsert({
              user_id: user.id,
              product_id: item.product.id,
              quantity: item.quantity,
            }, {
              onConflict: 'user_id,product_id'
            });
        }
      }
      // Clear local storage after sync
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      logger.error('Error syncing cart to database:', error);
    }
  }, [user, loadLocalCart]);

  // Track previous user state to detect login
  const prevUserRef = useRef<string | null>(null);
  
  // Initialize cart based on auth state
  useEffect(() => {
    const initCart = async () => {
      const prevUserId = prevUserRef.current;
      const currentUserId = user?.id || null;
      
      logger.debug('[Cart] Initializing, user:', currentUserId || 'guest', 'prev:', prevUserId || 'guest');
      
      // Detect login event (was guest, now authenticated)
      const justLoggedIn = !prevUserId && currentUserId;
      
      if (user) {
        if (justLoggedIn) {
          // User just logged in - preserve current items in state during sync
          const currentItems = items.length > 0 ? items : loadLocalCart();
          logger.debug('[Cart] User just logged in, preserving', currentItems.length, 'items');
          
          if (currentItems.length > 0) {
            // Keep current items visible while syncing
            setItems(currentItems);
            
            // Sync local items to database
            for (const item of currentItems) {
              try {
                await supabase
                  .from('cart_items')
                  .upsert({
                    user_id: user.id,
                    product_id: item.product.id,
                    quantity: item.quantity,
                    custom_unit: item.customUnit || null,
                  }, {
                    onConflict: 'user_id,product_id'
                  });
              } catch (error) {
                logger.error('[Cart] Error syncing item:', error);
              }
            }
            
            // Clear localStorage after successful sync
            localStorage.removeItem(CART_STORAGE_KEY);
            logger.debug('[Cart] Synced', currentItems.length, 'items to DB');
          }
        } else {
          // Already authenticated - load from DB
          await loadCartFromDb();
        }
        
        setDbLoaded(true);
        setIsInitialized(true);
        logger.debug('[Cart] Initialized for authenticated user');
      } else {
        // For guests: load from localStorage
        const localCart = loadLocalCart();
        logger.debug('[Cart] Loaded from localStorage:', localCart.length, 'items');
        setItems(localCart);
        setDbLoaded(false);
        setIsInitialized(true);
      }
      
      // Update previous user reference
      prevUserRef.current = currentUserId;
    };
    
    initCart();
  }, [user]); // Remove other deps to prevent re-running

  // Save to localStorage when items change (for guests only)
  // Only save after initialization to prevent overwriting with empty array
  useEffect(() => {
    if (!user && isInitialized) {
      saveLocalCart(items);
    }
  }, [items, user, saveLocalCart, isInitialized]);

  // Save to DB when items change (for authenticated users)
  const saveItemToDb = useCallback(async (item: CartItem) => {
    if (!user) return;
    
    logger.debug('[Cart] Saving item to DB:', item.product.id, 'qty:', item.quantity, 'unit:', item.customUnit);
    
    try {
      // Build delete query - handle null custom_unit properly
      let deleteQuery = supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', item.product.id);
      
      if (item.customUnit) {
        deleteQuery = deleteQuery.eq('custom_unit', item.customUnit);
      } else {
        deleteQuery = deleteQuery.is('custom_unit', null);
      }
      
      await deleteQuery;
      
      // Then insert the new/updated item
      const { error } = await supabase
        .from('cart_items')
        .insert({
          user_id: user.id,
          product_id: item.product.id,
          quantity: item.quantity,
          custom_unit: item.customUnit || null,
        });
      
      if (error) {
        logger.error('[Cart] Error saving cart item:', error);
      } else {
        logger.debug('[Cart] Item saved successfully');
      }
    } catch (error) {
      logger.error('[Cart] Error saving cart item:', error);
    }
  }, [user]);

  const addToCart = useCallback(async (product: Product) => {
    const updateItems = (prev: CartItem[]) => {
      const existing = prev.find(item => item.product.id === product.id && !item.customUnit);
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id && !item.customUnit
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    };

    setItems(prev => {
      const updated = updateItems(prev);
      // Save to DB
      if (user) {
        const item = updated.find(i => i.product.id === product.id && !i.customUnit);
        if (item) saveItemToDb(item);
      }
      return updated;
    });
  }, [user, saveItemToDb]);

  // Add to cart with a custom unit (e.g., "0.5 кг")
  // Items with same normalized unit will have their weights summed
  const addToCartWithCustomUnit = useCallback(async (product: Product, quantity: number, customUnit: string) => {
    const normalizedNewUnit = normalizeUnit(customUnit);
    
    // Parse weight from the new customUnit
    const newWeightMatch = normalizedNewUnit.match(/^([\d.]+)/);
    const newWeight = newWeightMatch ? parseFloat(newWeightMatch[1]) : 1;
    
    // Extract unit suffix (кг, л, г, etc.)
    const unitSuffix = customUnit.replace(/[\d.,\s]+/, '').trim() || product.unit;
    
    setItems(prev => {
      // Find existing item with same product and customUnit (weight-based items)
      const existingIndex = prev.findIndex(item => 
        item.product.id === product.id && 
        item.customUnit
      );
      
      let updated: CartItem[];
      if (existingIndex >= 0) {
        // Merge by adding weights together
        const existingItem = prev[existingIndex];
        const existingWeightMatch = normalizeUnit(existingItem.customUnit!).match(/^([\d.]+)/);
        const existingWeight = existingWeightMatch ? parseFloat(existingWeightMatch[1]) : 1;
        
        // Sum the weights
        const totalWeight = existingWeight + newWeight;
        const newCustomUnit = `${totalWeight} ${unitSuffix}`;
        
        updated = prev.map((item, i) =>
          i === existingIndex
            ? { ...item, customUnit: newCustomUnit, quantity: 1 }
            : item
        );
      } else {
        // Add new item
        updated = [...prev, { product, quantity: 1, customUnit }];
      }
      
      // Save to DB
      if (user) {
        const item = updated.find(i => i.product.id === product.id && i.customUnit);
        if (item) {
          // Delete old entry first, then save new
          (async () => {
            // Delete any existing entries for this product
            await supabase
              .from('cart_items')
              .delete()
              .eq('user_id', user.id)
              .eq('product_id', product.id);
            
            // Insert the new merged item
            await supabase
              .from('cart_items')
              .insert({
                user_id: user.id,
                product_id: product.id,
                quantity: item.quantity,
                custom_unit: item.customUnit,
              });
          })();
        }
      }
      
      return updated;
    });
  }, [user]);

  const removeFromCart = useCallback(async (productId: string, customUnit?: string) => {
    setItems(prev => prev.filter(item => {
      if (customUnit) {
        return !(item.product.id === productId && item.customUnit === customUnit);
      }
      return !(item.product.id === productId && !item.customUnit);
    }));

    if (user) {
      let deleteQuery = supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', productId);
      
      if (customUnit) {
        deleteQuery = deleteQuery.eq('custom_unit', customUnit);
      } else {
        deleteQuery = deleteQuery.is('custom_unit', null);
      }
      
      await deleteQuery;
    }
  }, [user]);

  const updateQuantity = useCallback(async (productId: string, quantity: number, customUnit?: string) => {
    if (quantity <= 0) {
      removeFromCart(productId, customUnit);
      return;
    }

    setItems(prev => {
      const updated = prev.map(item => {
        if (customUnit) {
          return (item.product.id === productId && item.customUnit === customUnit) 
            ? { ...item, quantity } 
            : item;
        }
        return (item.product.id === productId && !item.customUnit) 
          ? { ...item, quantity } 
          : item;
      });
      
      // Save to DB
      if (user) {
        const item = updated.find(i => {
          if (customUnit) {
            return i.product.id === productId && i.customUnit === customUnit;
          }
          return i.product.id === productId && !i.customUnit;
        });
        if (item) saveItemToDb(item);
      }
      
      return updated;
    });
  }, [user, removeFromCart, saveItemToDb]);

  // Update custom unit (change weight in cart)
  const updateCustomUnit = useCallback(async (productId: string, oldCustomUnit: string, newCustomUnit: string) => {
    const normalizedNewUnit = normalizeUnit(newCustomUnit);
    
    // Calculate the updated item before state update for DB sync
    let itemToSave: { quantity: number; customUnit: string } | null = null;
    
    setItems(prev => {
      // Find the old item first
      const oldItem = prev.find(item => 
        item.product.id === productId && item.customUnit === oldCustomUnit
      );
      
      if (!oldItem) return prev;
      
      // Check if there's already an item with the new unit
      const existingWithNewUnit = prev.find(item => 
        item.product.id === productId && 
        item.customUnit && 
        normalizeUnit(item.customUnit) === normalizedNewUnit &&
        item.customUnit !== oldCustomUnit
      );
      
      let updated: CartItem[];
      if (existingWithNewUnit) {
        // Merge: add quantity to existing, remove old
        updated = prev
          .map(item => {
            if (item.product.id === productId && item.customUnit && normalizeUnit(item.customUnit) === normalizedNewUnit && item.customUnit !== oldCustomUnit) {
              const mergedQuantity = item.quantity + oldItem.quantity;
              itemToSave = { quantity: mergedQuantity, customUnit: item.customUnit };
              return { ...item, quantity: mergedQuantity };
            }
            return item;
          })
          .filter(item => !(item.product.id === productId && item.customUnit === oldCustomUnit));
      } else {
        // Just update the unit
        updated = prev.map(item => {
          if (item.product.id === productId && item.customUnit === oldCustomUnit) {
            itemToSave = { quantity: item.quantity, customUnit: newCustomUnit };
            return { ...item, customUnit: newCustomUnit };
          }
          return item;
        });
      }
      
      // Immediately save to localStorage for persistence (guests)
      if (!user) {
        saveLocalCart(updated);
      }
      
      return updated;
    });

    // DB updates for authenticated users - use itemToSave which was set synchronously
    if (user && itemToSave) {
      try {
        // Delete old entry
        await supabase
          .from('cart_items')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', productId)
          .eq('custom_unit', oldCustomUnit);
        
        // Insert/update the new entry
        await supabase
          .from('cart_items')
          .upsert({
            user_id: user.id,
            product_id: productId,
            quantity: itemToSave.quantity,
            custom_unit: itemToSave.customUnit,
          });
      } catch (error) {
        logger.error('Error updating cart unit in DB:', error);
      }
    }
  }, [user, saveLocalCart]);

  const clearCart = useCallback(async () => {
    setItems([]);

    if (user) {
      await supabase
        .from('cart_items')
        .delete()
        .eq('user_id', user.id);
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [user]);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  
  const totalPrice = items.reduce((sum, item) => {
    // Use discounted price if promotion is active
    const effectivePrice = getDiscountedPrice(item.product);
    
    if (item.customUnit) {
      // For weight-based items: price = price_per_unit * weight (quantity is 1 for weight items)
      const weightMatch = normalizeUnit(item.customUnit).match(/^([\d.]+)/);
      const weight = weightMatch ? parseFloat(weightMatch[1]) : 1;
      return sum + effectivePrice * weight;
    }
    // For regular items: price * quantity
    return sum + effectivePrice * item.quantity;
  }, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        addToCartWithCustomUnit,
        removeFromCart,
        updateQuantity,
        updateCustomUnit,
        clearCart,
        totalItems,
        totalPrice,
        isLoading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
